import {
  KeyboardEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import ApplicationContext from "../../context/application/ApplicationContext";
import AuthContext from "../../context/auth/AuthContext";
import TenantContext from "../../context/tenant/TenantContext";
import JoinApplicationScreen from "../../components/JoinApplicationScreen/JoinApplicationScreen";
import GuarantorInviteSection, {
  GuarantorInvite,
} from "../../components/GuarantorInviteSection/GuarantorInviteSection";
import SkorDefaultLoader from "../../assets/img/skorDefaultLoader.gif";
import SkorProtectShield from "../../assets/img/skor-protect-shield.png";
import { isValidEmail, toast } from "../../helper/common";
import { sendUniversalMail } from "../../services/sharedService";
import useTranslator from "../../hooks/useTranslator";
import styles from "./CreateApplicationStep.module.css";
import SelfManagedGuarantorsModal from "../../components/SelfManagedGuarantorsModal/SelfManagedGuarantorsModal";

type InviteKind = "tenant" | "guarantor";

type Invite = {
  name: string;
  email: string;
  kind: InviteKind;
};

type InviteDraft = {
  name: string;
  email: string;
  error: string;
};

const emptyInviteDraft = (): InviteDraft => ({
  name: "",
  email: "",
  error: "",
});

type CreateApplicationStepProps = {
  onBack: () => void;
  updateTenantData: any;
  onCreated?: (payload: {
    title: string;
    peopleCount: number;
    pendingGuarantors?: { name: string; email?: string }[];
  }) => void;
};

const CheckIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="m4 10.5 3.4 3.4L16 5.8" />
  </svg>
);

const CreateApplicationStep = ({
  onBack,
  updateTenantData,
  onCreated,
}: CreateApplicationStepProps) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const {
    createApplication,
    consentApplication,
    pendingApplications,
    loadApplicationsData,
    applicationsLoaded,
  } = useContext(ApplicationContext);
  const { user } = useContext(AuthContext);
  const { createBasicGuarantorAccount, upsertSelectedOnboardingUsers } =
    useContext(TenantContext);
  const language = (localStorage.getItem("i18nextLng") || "en").toLowerCase();
  const defaultTitle = useMemo(
    () =>
      language.startsWith("es")
        ? `Aplicación de ${user?.fname || ""}`
        : `${user?.fname || ""}'s application`,
    [language, user?.fname]
  );
  const [title, setTitle] = useState(defaultTitle);
  const [editingTitle, setEditingTitle] = useState(false);
  const [description, setDescription] = useState(user?.about_me || "");
  const [showNote, setShowNote] = useState(!!user?.about_me);
  const [openForms, setOpenForms] = useState<Record<InviteKind, boolean>>({
    tenant: false,
    guarantor: false,
  });
  const [drafts, setDrafts] = useState<Record<InviteKind, InviteDraft>>({
    tenant: emptyInviteDraft(),
    guarantor: emptyInviteDraft(),
  });
  const [invites, setInvites] = useState<Invite[]>([]);
  const [guarantorInvites, setGuarantorInvites] = useState<GuarantorInvite[]>(
    []
  );
  const [selectedOnboardingUsers, setSelectedOnboardingUsers] = useState<any[]>(
    () =>
      Array.isArray(user?.tenant_extra_field_id?.selected_onboarding_users)
        ? user.tenant_extra_field_id.selected_onboarding_users
        : []
  );
  const [skorProtect, setSkorProtect] = useState(true);
  const [creating, setCreating] = useState(false);
  const [processingInvitation, setProcessingInvitation] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [showPendingOnRefresh, setShowPendingOnRefresh] = useState(false);
  const submittingRef = useRef(false);
  const titleChangedRef = useRef(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadApplicationsData();
    // ApplicationState recreates this function when its context updates.
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!titleChangedRef.current) setTitle(defaultTitle);
  }, [defaultTitle]);

  useEffect(() => {
    const latest = [...(pendingApplications ?? [])].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    setSelectedApplication(latest[0] ?? null);
  }, [pendingApplications]);

  useEffect(() => {
    const savedUsers = user?.tenant_extra_field_id?.selected_onboarding_users;
    if (Array.isArray(savedUsers)) setSelectedOnboardingUsers(savedUsers);
  }, [user?._id, user?.tenant_extra_field_id?.selected_onboarding_users]);

  useEffect(() => {
    if (!user?.profile_completed) return;
    const selected = (
      user?.tenant_extra_field_id?.selected_onboarding_users ?? []
    ).filter((u: any) => u?.type === "guarantor");
    if (selected.length === 0) return;
    const added = user?.gurantor_added_id ?? [];
    const completedUsers: any[] =
      user?.tenant_extra_field_id?.completed_onboarding_users ?? [];
    const normEmail = (v: any) =>
      typeof v === "string" ? v.toLowerCase() : "";
    const normName = (v: any) =>
      typeof v === "string" ? v.trim().toLowerCase() : "";
    const isCompleted = (s: any) => {
      const email = normEmail(s.email);
      if (email) {
        if (
          completedUsers.some(
            (c: any) => c?.type === "guarantor" && normEmail(c?.email) === email
          )
        )
          return true;
        if (
          added.some(
            (a: any) =>
              normEmail(a?.gurantor_email) === email ||
              normEmail(a?.guarantor_id?.email) === email
          )
        )
          return true;
      }
      const name = normName(s.name);
      if (name) {
        if (
          completedUsers.some(
            (c: any) => c?.type === "guarantor" && normName(c?.name) === name
          )
        )
          return true;
        if (
          added.some(
            (a: any) =>
              normName(
                `${a?.guarantor_id?.fname ?? ""} ${a?.guarantor_id?.lname ?? ""}`.trim()
              ) === name
          )
        )
          return true;
      }
      return false;
    };
    const pending = selected.filter((s: any) => !isCompleted(s));
    if (pending.length > 0) setShowPendingOnRefresh(true);
  }, [
    user?.profile_completed,
    user?.tenant_extra_field_id?.selected_onboarding_users,
    user?.gurantor_added_id,
  ]);

  const updateDraft = (kind: InviteKind, patch: Partial<InviteDraft>) => {
    setDrafts((current) => ({
      ...current,
      [kind]: { ...current[kind], ...patch },
    }));
  };

  const openInviteForm = (kind: InviteKind) => {
    setOpenForms((current) => ({ ...current, [kind]: true }));
    updateDraft(kind, { error: "" });
  };

  const closeInviteForm = (kind: InviteKind) => {
    setOpenForms((current) => ({ ...current, [kind]: false }));
    setDrafts((current) => ({ ...current, [kind]: emptyInviteDraft() }));
  };

  const addInvite = (kind: InviteKind) => {
    const draft = drafts[kind];
    const trimmedName = draft.name.trim();
    const trimmedEmail = draft.email.trim().toLowerCase();

    if (!trimmedName) {
      updateDraft(kind, {
        error: _t("nameRequired", "Full name is required"),
      });
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      updateDraft(kind, {
        error: _t("invalidEmail", "Enter a valid email address"),
      });
      return;
    }
    if (trimmedEmail === user?.email?.toLowerCase()) {
      updateDraft(kind, {
        error: _t("cannotAddYourself", "You cannot add yourself"),
      });
      return;
    }
    if (invites.some((invite) => invite.email === trimmedEmail)) {
      updateDraft(kind, {
        error: _t("emailAlreadyAdded", "This email has already been added"),
      });
      return;
    }
    if (
      kind === "tenant" &&
      invites.filter((invite) => invite.kind === "tenant").length >= 4
    ) {
      updateDraft(kind, {
        error: _t("maxParticipantsReached", "Maximum of 4 tenants allowed"),
      });
      return;
    }

    setInvites((current) => [
      ...current,
      {
        name: trimmedName,
        email: trimmedEmail,
        kind,
      },
    ]);
    closeInviteForm(kind);
  };

  const handleInviteKeyDown = (
    kind: InviteKind,
    event: KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      addInvite(kind);
    }
  };

  const tenantInvites = invites.filter(({ kind }) => kind === "tenant");
  const existingGuarantors = user?.gurantor_added_id ?? [];
  const peopleCount =
    1 +
    tenantInvites.length +
    guarantorInvites.length +
    existingGuarantors.length;

  const submitGuarantors = async (invitesToSubmit: GuarantorInvite[]) => {
    const emailInvites = invitesToSubmit.filter(
      ({ guarantorMethod }) => guarantorMethod !== "self"
    );
    const selfManagedGuarantors = invitesToSubmit.filter(
      ({ guarantorMethod }) => guarantorMethod === "self"
    );

    for (const guarantor of emailInvites) {
      const [fname, ...surname] = guarantor.name.split(/\s+/);
      try {
        await createBasicGuarantorAccount({
          fname,
          lname: surname.join(" "),
          email: guarantor.email,
          relation: "other",
          step: 1,
        });
      } catch (err: any) {
        if (err?.response?.data?.errorKey !== "account-already-present") {
          throw err;
        }
      }

      await sendUniversalMail({
        receiverEmail: guarantor.email,
        senderId: localStorage.getItem("saveduserid"),
        emailType: "score",
        status: "processing",
      });
    }

    if (selfManagedGuarantors.length > 0) {
      const newSelectedUsers = selfManagedGuarantors
        .map(({ name, email }) => ({ name, type: "guarantor", email }))
        .filter(
          ({ name, type }) =>
            !selectedOnboardingUsers.some(
              (savedUser) =>
                savedUser?.type === type && savedUser?.name === name
            )
        );
      const nextSelectedUsers = [
        ...selectedOnboardingUsers,
        ...newSelectedUsers,
      ];
      const result = await upsertSelectedOnboardingUsers({
        tenant_id: user?._id ?? localStorage.getItem("saveduserid"),
        users: nextSelectedUsers,
      });
      if (result === null) throw new Error("failed-to-add-guarantor");
      setSelectedOnboardingUsers(nextSelectedUsers);
    }
  };

  const handleCreate = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setCreating(true);

    try {
      await updateTenantData({
        about_me: description,
        skor_protect: skorProtect,
        is_edit: false,
        profile_completed: true,
        role: localStorage.getItem("usertype"),
      });
      await submitGuarantors(guarantorInvites);
      if (tenantInvites.length > 0) {
        await createApplication({
          participants: tenantInvites.map(
            ({ name: inviteName, email: inviteEmail }) => ({
              name: inviteName,
              email: inviteEmail,
            })
          ),
          title: title.trim() || defaultTitle,
          skor_protect: skorProtect,
          description: description.trim(),
        });
      }
      const createdTitle = title.trim() || defaultTitle;
      const pendingGuarantors = guarantorInvites
        .filter((g) => g.guarantorMethod === "self")
        .map((g) => ({ name: g.name, email: g.email }));
      // also include previously selected self-managed already stored
      const existingPending = selectedOnboardingUsers
        .filter((u: any) => u?.type === "guarantor")
        .map((u: any) => ({ name: u.name, email: u.email }));
      const allPending = [
        ...existingPending,
        ...pendingGuarantors.filter(
          (p) =>
            !existingPending.some((e) =>
              e.email
                ? e.email.toLowerCase() === p.email.toLowerCase()
                : e.name?.trim().toLowerCase() === p.name.trim().toLowerCase()
            )
        ),
      ];
      if (onCreated)
        onCreated({
          title: createdTitle,
          peopleCount,
          pendingGuarantors: allPending,
        });
      else navigate("/home?welcome=true");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ??
          _t("failedToCreateApplication", "Failed to create application")
      );
    } finally {
      submittingRef.current = false;
      setCreating(false);
    }
  };

  const handleAcceptInvitation = async (
    joinGuarantorInvites: GuarantorInvite[]
  ) => {
    if (!selectedApplication) return;
    setProcessingInvitation(true);
    try {
      await submitGuarantors(joinGuarantorInvites);
      await consentApplication(selectedApplication._id, "accepted");
      await updateTenantData({
        is_edit: false,
        profile_completed: true,
        role: localStorage.getItem("usertype"),
      });
      const joinedTitle = selectedApplication?.title || defaultTitle;
      if (onCreated) onCreated({ title: joinedTitle, peopleCount });
      else navigate("/home?welcome=true");
    } catch {
      toast.error(_t("failedToJoinGroup", "Failed to join application"));
    } finally {
      setProcessingInvitation(false);
    }
  };

  if (!applicationsLoaded) {
    return (
      <div className={`${styles.overlay} modal-fixed-screen`}>
        <img src={SkorDefaultLoader} className={styles.loader} alt="" />
      </div>
    );
  }

  if (selectedApplication) {
    return (
      <div className={`${styles.overlay} modal-fixed-screen`}>
        <JoinApplicationScreen
          app={selectedApplication}
          onAccept={handleAcceptInvitation}
          onBack={onBack}
          processing={processingInvitation}
          user={user}
          _t={_t}
        />
      </div>
    );
  }

  const renderInviteForm = (kind: InviteKind) => {
    if (!openForms[kind]) return null;
    const draft = drafts[kind];
    return (
      <div className={styles.inviteForm}>
        <div className={styles.inviteFormHeader}>
          <span className={styles.inviteFormBadge} aria-hidden="true">
            {kind === "tenant"
              ? _t("tenant", "Tenant")
              : _t("guarantor", "Guarantor")}
          </span>
        </div>
        <input
          value={draft.name}
          autoFocus
          onChange={(event) =>
            updateDraft(kind, { name: event.target.value, error: "" })
          }
          onKeyDown={(event) => handleInviteKeyDown(kind, event)}
          placeholder={_t("fullName", "Full name")}
          aria-label={_t("fullName", "Full name")}
        />
        <input
          type="email"
          value={draft.email}
          onChange={(event) =>
            updateDraft(kind, {
              email: event.target.value.toLowerCase(),
              error: "",
            })
          }
          onKeyDown={(event) => handleInviteKeyDown(kind, event)}
          placeholder="email@example.com"
          aria-label="Email"
        />
        <button
          className={styles.addButton}
          type="button"
          onClick={() => addInvite(kind)}
          disabled={!draft.name.trim() || !draft.email.trim()}
        >
          {_t("add", "Add")}
        </button>
        <button
          className={styles.deleteButton}
          type="button"
          onClick={() => closeInviteForm(kind)}
          aria-label={_t("remove", "Remove")}
        >
          <img src="/images/delete-icon.svg" alt="" />
        </button>
        {draft.error && <p className={styles.formError}>{draft.error}</p>}
      </div>
    );
  };

  if (showPendingOnRefresh) {
    const selectedRaw = selectedOnboardingUsers.filter(
      (u: any) => u?.type === "guarantor"
    ) as any[];
    const added = user?.gurantor_added_id ?? [];
    const completedUsers: any[] =
      user?.tenant_extra_field_id?.completed_onboarding_users ?? [];
    const normEmail = (v: any) =>
      typeof v === "string" ? v.toLowerCase() : "";
    const normName = (v: any) =>
      typeof v === "string" ? v.trim().toLowerCase() : "";
    const keyOf = (g: any) => normEmail(g.email) || normName(g.name);
    // Merge selected + completed guarantors so completed ones (e.g. fhfk) appear even if not in selected
    const mergedMap = new Map<string, any>();
    for (const g of selectedRaw) { const k = keyOf(g); if (k && !mergedMap.has(k)) mergedMap.set(k, g); }
    for (const c of completedUsers.filter((c: any) => c?.type === "guarantor")) { const k = keyOf(c); if (k && !mergedMap.has(k)) mergedMap.set(k, { name: c.name, email: c.email, type: "guarantor" }); }
    const selected = Array.from(mergedMap.values());
    const pending = selected.map((p: any) => {
      const email = normEmail(p.email);
      const name = normName(p.name);
      let completed = false;
      if (email) {
        if (
          completedUsers.some(
            (c: any) => c?.type === "guarantor" && normEmail(c?.email) === email
          )
        )
          completed = true;
        else if (
          added.some(
            (a: any) =>
              normEmail(a?.gurantor_email) === email ||
              normEmail(a?.guarantor_id?.email) === email
          )
        )
          completed = true;
      }
      if (!completed && name) {
        if (
          completedUsers.some(
            (c: any) => c?.type === "guarantor" && normName(c?.name) === name
          )
        )
          completed = true;
        else if (
          added.some((a: any) => {
            const an = normName(
              `${a?.guarantor_id?.fname ?? ""} ${a?.guarantor_id?.lname ?? ""}`.trim()
            );
            return !!an && an === name;
          })
        )
          completed = true;
      }
      return { ...p, completed };
    });
    return (
      <div className={`${styles.overlay} modal-fixed-screen`}>
        <SelfManagedGuarantorsModal
          guarantors={pending}
          onLater={() => setShowPendingOnRefresh(false)}
          inline
        />
      </div>
    );
  }

  return (
    <div className={`${styles.overlay} modal-fixed-screen`}>
      <main className={`${styles.card} onboarding-modal-surface`}>
        <button className={styles.backButton} type="button" onClick={onBack}>
          <span aria-hidden="true">‹</span> {_t("back", "Back")}
        </button>

        <h1>{_t("createYourApplication", "Create your application")}</h1>

        <label className={styles.titleLabel} htmlFor="application-title">
          {_t("applicationName", "Application name")}
        </label>
        <div className={styles.titleField}>
          {editingTitle ? (
            <input
              id="application-title"
              ref={titleInputRef}
              value={title}
              maxLength={80}
              onChange={(event) => {
                titleChangedRef.current = true;
                setTitle(event.target.value);
              }}
              onBlur={() => {
                setTitle((current) => {
                  if (current.trim()) return current.trim();
                  titleChangedRef.current = false;
                  return defaultTitle;
                });
                setEditingTitle(false);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") event.currentTarget.blur();
              }}
            />
          ) : (
            <span>{title}</span>
          )}
          <button
            type="button"
            onClick={() => {
              setEditingTitle(true);
              requestAnimationFrame(() => titleInputRef.current?.focus());
            }}
            aria-label={_t("editApplicationName", "Edit application name")}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m4 20 4.2-1 10.4-10.4a2.4 2.4 0 0 0-3.4-3.4L4.8 15.6 4 20Zm9.8-13.4 3.4 3.4" />
            </svg>
          </button>
        </div>

        <p className={styles.intro}>
          {_t(
            "addPeopleToRentalApplication",
            "Add the people who'll be part of your rental. Each one gets an email invitation, or you can fill in their profile."
          )}
        </p>

        <section className={styles.people} aria-label={_t("people", "People")}>
          <div className={styles.personRow}>
            <span className={styles.avatar}>
              {(user?.fname || user?.email || "Y").charAt(0).toUpperCase()}
            </span>
            <div>
              <div className={styles.personName}>
                <strong>{_t("you", "You")}</strong>
                <span>{_t("leadTenant", "Lead tenant")}</span>
              </div>
              <p>{user?.email}</p>
            </div>
          </div>

          {(
            selectedOnboardingUsers.filter(
              (u: any) => u?.type === "guarantor"
            ) as any[]
          ).map((g: any) => (
            <div
              className={`${styles.addedPerson} ${styles.addedPersonIndented}`}
              key={g.email || g.name}
            >
              <span className={styles.avatar}>
                {(g.name || g.email || "G").charAt(0).toUpperCase()}
              </span>
              <div>
                <strong>{g.name}</strong>
                <p>
                  {g.email} · {_t("guarantor", "Guarantor")}
                </p>
              </div>
            </div>
          ))}
          <GuarantorInviteSection
            currentUserEmail={user?.email}
            invites={guarantorInvites}
            onChange={setGuarantorInvites}
            _t={_t}
          />

          <div className={styles.tenantDivider} />

          {tenantInvites.map((invite) => (
            <div className={styles.addedPerson} key={invite.email}>
              <span className={styles.avatar}>{invite.name.charAt(0)}</span>
              <div>
                <strong>{invite.name}</strong>
                <p>{invite.email}</p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setInvites((current) =>
                    current.filter(
                      ({ email: inviteEmail }) => inviteEmail !== invite.email
                    )
                  )
                }
                aria-label={_t("removeTenant", "Remove tenant")}
              >
                ×
              </button>
            </div>
          ))}

          {openForms.tenant ? (
            renderInviteForm("tenant")
          ) : (
            <button
              className={styles.addPersonButton}
              type="button"
              onClick={() => openInviteForm("tenant")}
              disabled={tenantInvites.length >= 4}
            >
              <span>+</span>
              <strong>{_t("addTenant", "Add tenant")}</strong>
            </button>
          )}
        </section>

        {!showNote ? (
          <button
            type="button"
            className={styles.addNoteButton}
            onClick={() => setShowNote(true)}
          >
            +{" "}
            {_t(
              "addANoteAboutHousehold",
              "Add a note about your household (optional)"
            )}
          </button>
        ) : (
          <div className={styles.noteSection}>
            <div className={styles.noteHeader}>
              <span>
                {_t(
                  "addANoteAboutHousehold",
                  "Add a note about your household (optional)"
                )}
              </span>
              <button
                type="button"
                className={styles.noteClose}
                onClick={() => {
                  setShowNote(false);
                  setDescription("");
                }}
                aria-label={_t("remove", "Remove")}
              >
                ×
              </button>
            </div>
            <textarea
              className={styles.description}
              value={description}
              maxLength={500}
              autoFocus
              onChange={(event) => setDescription(event.target.value)}
              placeholder={_t(
                "applicationDescriptionExample",
                "E.g. We're a couple with permanent contracts, relocating for work in March."
              )}
              aria-label={_t(
                "applicationDescription",
                "Application description"
              )}
            />
          </div>
        )}

        <label
          className={`${styles.protectCard} ${skorProtect ? styles.protectCardActive : ""}`}
        >
          <input
            type="checkbox"
            checked={skorProtect}
            onChange={(event) => setSkorProtect(event.target.checked)}
          />
          <span className={styles.customCheckbox} aria-hidden="true">
            {skorProtect && <CheckIcon />}
          </span>
          <div className={styles.protectContent}>
            <div className={styles.protectHeading}>
              <span className={styles.shield}>
                <img src={SkorProtectShield} alt="" />
              </span>
              <strong>{_t("skorProtectTitle", "Skor Protect")}</strong>
              <span className={styles.badge}>
                {_t("recommended", "Recommended")}
              </span>
            </div>
            <p>
              {_t(
                "skorProtectDesc",
                "Signal to landlords that rent is backed by Skor, boosting your chances of getting the property."
              )}
            </p>
            <ul>
              <li>
                <CheckIcon />
                <span>
                  <strong>
                    {_t("skorBenefit1Bold", "Landlords trust you more")}
                  </strong>{" "}
                  - {_t("skorBenefit1Rest", "rent is backed")}
                </span>
              </li>
              <li>
                <CheckIcon />
                <span>
                  <strong>{_t("skorBenefit2Bold", "You're covered")}</strong> -{" "}
                  {_t(
                    "skorBenefit2Rest",
                    "if a dispute with your landlord arises we help you"
                  )}
                </span>
              </li>
              <li>
                <CheckIcon />
                <span>
                  <strong>
                    {_t("skorBenefit3Bold", "Stronger candidacy")}
                  </strong>{" "}
                  - {_t("skorBenefit3Rest", "visible on your shared profile")}
                </span>
              </li>
            </ul>
            <p className={styles.pricing}>
              {_t("skorNoChargeNow", "No charge now")} ·{" "}
              <u>
                {_t(
                  "skorPriceConfirmed",
                  "Price confirmed once the rental amount is set"
                )}
              </u>
            </p>
          </div>
        </label>

        <footer className={styles.footer}>
          <p>
            {peopleCount}{" "}
            {peopleCount === 1
              ? _t("person", "person")
              : _t("people", "people")}{" "}
            {_t("inThisApplication", "in this application")}.
          </p>
          <button type="button" onClick={handleCreate} disabled={creating}>
            {creating
              ? `${_t("creating", "Creating")}...`
              : _t("createApplication", "Create application")}
          </button>
        </footer>
      </main>
    </div>
  );
};

export default CreateApplicationStep;
